import {
  Body,
  Button,
  Heading,
  IconButton,
  Spinner,
} from '@assembly-js/design-system';
import { t } from 'i18next';
import React, { useState } from 'react';

import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ModalWrapper } from 'src/legacy/components/Modals';
import type { ModalWrapperProps } from 'src/legacy/components/Modals/ModalWrapper';

import { BillingIntervalToggle } from './BillingIntervalToggle';
import { PlanChangeUpgradeModal } from './PlanChangeUpgradeModal';
import { PlanComparisonTable } from './PlanComparisonTable';
import { getStudioPlanTitle } from './studioFeatureGating';
import { SELLABLE_PLAN_IDS } from './studioPlanComparison';
import {
  classifyPlanSelection,
  formatDollars,
  getStudioPlanDisplays,
  isStudioPlanCardVisible,
  monthlyPriceCents,
  SellableStudioPlanId,
  StudioBaseTiers,
  StudioBillingInterval,
  StudioPlanPricingState,
  toPlanChangeSelection,
} from './studioPlanFeatures';
import { UpgradePlanSelectorCard } from './UpgradePlanSelectorCard';
import { useStudioPlanChange } from './useStudioPlanChange';

interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  pricing: StudioPlanPricingState;
  initialPlanId?: SellableStudioPlanId;
}

const DESKTOP_WIDTH_PX = 1160;
const DESKTOP_HEIGHT_PX = 756;
const DESKTOP_RADIUS_PX = 8;
const DESKTOP_SHADOW = '0px 6px 20px 0px rgba(0, 0, 0, 0.07)';
const PLAN_RADIO_GROUP = 'upgrade-plan';
// The design opens on Pro; it is also the plan the modal title/CTA name.
const DEFAULT_PLAN_ID: SellableStudioPlanId = 'professional';

export const UpgradePlanModal: React.FC<UpgradePlanModalProps> = ({
  isOpen,
  onClose,
  pricing,
  initialPlanId = DEFAULT_PLAN_ID,
}) => {
  const isMobile = useMediaQuery('sm', 'max');
  const [interval, setInterval] = useState<StudioBillingInterval>('month');
  const [requestedPlanId, setRequestedPlanId] =
    useState<SellableStudioPlanId>(initialPlanId);
  const {
    currentPlan,
    selection,
    activeModal,
    isUpgrading,
    choosePlan,
    confirmUpgrade,
    closeModals,
  } = useStudioPlanChange({ onUpgradeComplete: onClose });

  // The confirmation step replaces this modal rather than stacking on top of
  // it; cancelling there comes back to the same plan and interval.
  const isConfirmingUpgrade =
    activeModal === 'upgrade' && selection !== undefined;

  // A plan the catalog did not price has no selector card and no price, so the
  // selection is clamped to the ones it did — otherwise the title, CTA and
  // highlighted column would name a plan this modal cannot actually offer.
  // Plans below the current one are left out for the same reason the plans grid
  // hides them: this is an upgrade surface, and giving less belongs to the
  // manage-plan flow, which has the survey step this modal doesn't.
  const offeredPlanIds = (
    pricing.status === 'ready'
      ? SELLABLE_PLAN_IDS.filter((planId) => pricing.baseTiers.has(planId))
      : SELLABLE_PLAN_IDS
  ).filter((planId) => isStudioPlanCardVisible(planId, currentPlan));
  const selectedPlanId = offeredPlanIds.includes(requestedPlanId)
    ? requestedPlanId
    : (offeredPlanIds.at(0) ?? initialPlanId);

  const selectedSubTitle = getStudioPlanDisplays().find(
    (display) => display.id === selectedPlanId,
  )?.subTitle;
  const selectedPlanTitle = getStudioPlanTitle(selectedPlanId);
  const title = t(($) => $.page_studio_plans.upgrade_modal.title, {
    plan: selectedPlanTitle,
  });

  const handleWrapperClose: ModalWrapperProps['onClose'] = () => onClose();

  const renderPlans = (baseTiers: StudioBaseTiers) => {
    const selectedBaseTier = baseTiers.get(selectedPlanId);
    const upgradeTarget = selectedBaseTier
      ? toPlanChangeSelection(selectedPlanId, interval, selectedBaseTier)
      : undefined;
    // Undefined means there is nothing to submit — the current plan hasn't
    // resolved yet, or the selection already matches it — so the CTA waits
    // rather than clicking blind. A gated give-more (an annual target, which
    // the upgrade endpoint rejects) stays clickable: choosePlan answers it with
    // its own message.
    const changeKind = upgradeTarget
      ? classifyPlanSelection(upgradeTarget, currentPlan)
      : undefined;

    return (
      <div className="flex h-full w-full flex-col overflow-y-auto sm:flex-row sm:overflow-hidden">
        {/* On desktop the left panel has no right padding; the table panel owns
            that 32px gutter. Mobile keeps the narrower gutter and 20px rhythm of
            its own frame. */}
        <div className="flex flex-col gap-5 p-4 pb-5 sm:w-80 sm:shrink-0 sm:gap-8 sm:overflow-y-auto sm:p-8 sm:pb-12 sm:pr-0">
          <div className="flex flex-col gap-1">
            <Heading
              tag="h2"
              size="lg"
              className="!mb-0 pr-8 leading-6 text-black-heading sm:text-[20px] sm:leading-7"
            >
              {title}
            </Heading>
            {selectedSubTitle && (
              <Body className="leading-[22px] text-gray-small">
                {selectedSubTitle}
              </Body>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <BillingIntervalToggle
              interval={interval}
              onChangeInterval={setInterval}
            />

            <div className="flex flex-col gap-2">
              <Body
                size="sm"
                tag="span"
                className="leading-[21px] text-gray-small"
              >
                {t(($) => $.page_studio_plans.upgrade_modal.choose_plan)}
              </Body>
              <div className="flex flex-col gap-3">
                {offeredPlanIds.map((planId) => {
                  const baseTier = baseTiers.get(planId);
                  if (!baseTier) return null;
                  const priceLabel = `${formatDollars(
                    monthlyPriceCents(baseTier, interval),
                  )}${t(($) => $.page_studio_plans.upgrade_modal.per_month_short)}`;
                  return (
                    <UpgradePlanSelectorCard
                      key={planId}
                      planId={planId}
                      title={getStudioPlanTitle(planId)}
                      priceLabel={priceLabel}
                      selected={planId === selectedPlanId}
                      onSelect={setRequestedPlanId}
                      groupName={PLAN_RADIO_GROUP}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <Button
            variant="primary"
            size="sm"
            label={t(($) => $.page_studio_plans.upgrade_modal.cta, {
              plan: selectedPlanTitle,
            })}
            className="box-border w-full"
            disabled={changeKind === undefined}
            onClick={() => {
              if (upgradeTarget) {
                choosePlan(upgradeTarget);
              }
            }}
          />
        </div>

        <div className="px-4 pb-4 pt-0 sm:flex-1 sm:overflow-y-auto sm:px-8 sm:pb-0 sm:pt-10">
          <PlanComparisonTable
            planIds={isMobile ? [selectedPlanId] : offeredPlanIds}
            baseTiers={baseTiers}
            highlightPlanId={selectedPlanId}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (pricing.status) {
      case 'loading':
        return (
          <div className="flex h-full min-h-[240px] w-full items-center justify-center">
            <Spinner size={10} />
          </div>
        );
      case 'error':
        return (
          <div className="flex h-full min-h-[240px] w-full flex-col items-center justify-center gap-3 p-8">
            <Body className="text-gray-small">
              {t(($) => $.page_studio_plans.load_error)}
            </Body>
            <Button
              variant="secondary"
              size="sm"
              label={t(($) => $.page_studio_plans.retry)}
              onClick={pricing.onRetry}
            />
          </div>
        );
      case 'ready':
        return renderPlans(pricing.baseTiers);
    }
  };

  return (
    <>
      <ModalWrapper
        open={isOpen && !isConfirmingUpgrade}
        onClose={handleWrapperClose}
        hideActions
        hideDividers
        disableGutters
        fullWidth={isMobile}
        width={isMobile ? undefined : DESKTOP_WIDTH_PX}
        height={isMobile ? '100%' : DESKTOP_HEIGHT_PX}
        sx={
          isMobile
            ? {
                '& .MuiDialog-paper': {
                  m: 0,
                  maxWidth: '100%',
                  maxHeight: '100%',
                  border: 'none',
                  borderRadius: 0,
                  height: '100%',
                },
              }
            : {
                '& .MuiDialog-paper': {
                  borderRadius: `${DESKTOP_RADIUS_PX}px`,
                  boxShadow: DESKTOP_SHADOW,
                },
              }
        }
        aria-label={title}
      >
        <div className="relative h-full w-full bg-white">
          <IconButton
            icon="Close"
            variant="minimal"
            size="sm"
            aria-label={t(($) => $.page_studio_plans.upgrade_modal.close)}
            onClick={onClose}
            className="absolute right-4 top-4 z-10 rounded hover:bg-gray-150 sm:right-2 sm:top-2 sm:rounded-full sm:bg-gray-100"
          />
          {renderContent()}
        </div>
      </ModalWrapper>
      {selection && (
        <PlanChangeUpgradeModal
          isOpen={isConfirmingUpgrade}
          selection={selection}
          isLoading={isUpgrading}
          onClose={closeModals}
          onConfirm={(totalCredits) => {
            void confirmUpgrade(totalCredits);
          }}
        />
      )}
    </>
  );
};
